import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Alert,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import {
  usePendingApprovalsQuery,
  useBatchApproveMutation,
  useApproveDeletionMutation,
  useRejectDeletionMutation,
} from '../../hooks/queries';
import { ChangeRequestList } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb, EmptyState } from '../../components';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { MainStackParamList } from '../../navigation/MainNavigator';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

interface PendingCounts {
  goals: number;
  initiatives: number;
  kpis: number;
  processes: number;
  sub_processes: number;
  services: number;
  activities: number;
  unit_goals: number;
}

interface TypeConfig {
  key: keyof PendingCounts;
  labelAr: string;
  labelEn: string;
  color: string;
  iconPath: string;
}

const TYPE_CONFIGS: TypeConfig[] = [
  {
    key: 'goals',
    labelAr: 'الأهداف الاستراتيجية',
    labelEn: 'Strategic Goals',
    color: '#A9211F',
    iconPath: 'M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  },
  {
    key: 'initiatives',
    labelAr: 'المبادرات',
    labelEn: 'Initiatives',
    color: '#3B82F6',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
  },
  {
    key: 'kpis',
    labelAr: 'مؤشرات الأداء',
    labelEn: 'KPIs',
    color: '#22C55E',
    iconPath: 'M3 3v18h18M9 17V9m4 8V5m4 12v-4',
  },
  {
    key: 'processes',
    labelAr: 'العمليات',
    labelEn: 'Processes',
    color: '#8B5CF6',
    iconPath: 'M4 6h16M4 12h16M4 18h16',
  },
  {
    key: 'sub_processes',
    labelAr: 'العمليات الفرعية',
    labelEn: 'Sub-Processes',
    color: '#A855F7',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  },
  {
    key: 'services',
    labelAr: 'الخدمات',
    labelEn: 'Services',
    color: '#F59E0B',
    iconPath: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  },
  {
    key: 'activities',
    labelAr: 'الأنشطة',
    labelEn: 'Activities',
    color: '#EC4899',
    iconPath: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    key: 'unit_goals',
    labelAr: 'أهداف الوحدات التنظيمية',
    labelEn: 'Unit Goals',
    color: '#06B6D4',
    iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
  },
];

// Back Arrow Icon
const ArrowRightIcon = ({ size = 24, color = '#111827' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Checkmark icon for approve button
const CheckAllIcon = ({ size = 24, color = '#FFFFFF' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

export default function PendingApprovalsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === 'ar';

  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textMuted: colors.textMuted,
  };

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const successTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [successCounts, setSuccessCounts] = useState<PendingCounts | null>(null);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const departmentId = user?.profile?.organization;
  const userType = user?.profile?.user_type;
  // DTS 2.2: Section Head is the proposer, NOT an approver. Both MANAGER
  // and DIRECTOR map to 'org_executive', so the legacy userType check
  // exposed this screen to Section Head. Gate on hrcpms.review.approve_reject
  // (held by Director + Strategy Team) instead.
  const canApprove = !!(
    user?.is_superuser
    || (user?.permissions || []).includes('hrcpms.review.approve_reject')
  );

  // Redirect unauthorized users (Section Head, employees, view-only roles)
  useEffect(() => {
    if (!canApprove) {
      navigation.goBack();
    }
  }, [canApprove, navigation]);

  // React Query owns the approvals + deletion-requests fetch. Mutations
  // invalidate the queryKey on success, so the list refreshes automatically
  // — no manual `await fetchPendingApprovals()` threading needed.
  const {
    data: pendingBundle,
    isPending: loading,
    isRefetching: refreshing,
    refetch,
  } = usePendingApprovalsQuery(canApprove);

  const pendingCounts = useMemo<PendingCounts>(() => {
    const data = (pendingBundle?.approvals as any) ?? {};
    return {
      goals: data.strategic_goals?.length ?? data.goals_count ?? 0,
      initiatives: data.initiatives?.length ?? data.initiatives_count ?? 0,
      kpis: data.kpis?.length ?? data.kpis_count ?? 0,
      processes: data.processes?.length ?? data.processes_count ?? 0,
      sub_processes: data.sub_processes?.length ?? data.sub_processes_count ?? 0,
      services: data.services?.length ?? data.services_count ?? 0,
      activities: data.activities?.length ?? data.activities_count ?? 0,
      unit_goals: data.unit_goals?.length ?? data.unit_goals_count ?? 0,
    };
  }, [pendingBundle]);

  const deletionRequests: ChangeRequestList[] = pendingBundle?.deletionRequests ?? [];
  const totalPending = Object.values(pendingCounts).reduce((sum, count) => sum + count, 0);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  const onRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const batchApproveMutation = useBatchApproveMutation();
  const approveDeletionMutation = useApproveDeletionMutation();
  const rejectDeletionMutation = useRejectDeletionMutation();
  const approving = batchApproveMutation.isPending;

  const handleBatchApprove = async () => {
    if (!departmentId) return;
    setShowConfirmModal(false);
    const prevCounts = { ...pendingCounts };
    try {
      await batchApproveMutation.mutateAsync(departmentId);
      setSuccessCounts(prevCounts);
      setShowSuccessMessage(true);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => setShowSuccessMessage(false), 4000);
    } catch (error: any) {
      console.error('Error batch approving:', error);
      const msg = error?.response?.data?.detail || error?.response?.data?.message || (isRTL ? 'حدث خطأ أثناء الاعتماد' : 'Error during approval');
      Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
    }
  };

  const handleApproveDeletion = async (id: number) => {
    setApprovingId(id);
    try {
      await approveDeletionMutation.mutateAsync(id);
    } catch (error: any) {
      console.error('Error approving deletion:', error);
      const msg = error?.response?.data?.detail || (isRTL ? 'حدث خطأ أثناء الموافقة' : 'Error during approval');
      Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
    } finally {
      setApprovingId(null);
    }
  };

  const handleRejectDeletion = async (id: number) => {
    setRejectingId(id);
    try {
      await rejectDeletionMutation.mutateAsync({
        id,
        reason: isRTL ? 'مرفوض' : 'Rejected',
      });
    } catch (error: any) {
      console.error('Error rejecting deletion:', error);
      const msg = error?.response?.data?.detail || (isRTL ? 'حدث خطأ أثناء الرفض' : 'Error during rejection');
      Alert.alert(isRTL ? 'خطأ' : 'Error', msg);
    } finally {
      setRejectingId(null);
    }
  };

  const activeTypes = TYPE_CONFIGS.filter((config) => pendingCounts[config.key] > 0);

  if (loading || !canApprove) {
    return <LoadingScreen />;
  }

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: isRTL ? 'الرئيسية' : 'Home' },
          { label: isRTL ? 'الموافقات المعلقة' : 'Pending Approvals' },
        ]}
      />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: themeColors.card, borderBottomColor: themeColors.cardBorder, flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={styles.headerContent}>
          <Text style={[styles.headerTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
            {isRTL ? 'الموافقات المعلقة' : 'Pending Approvals'}
          </Text>
          <Text style={[styles.headerSubtitle, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
            {totalPending} {isRTL ? 'عنصر بانتظار الاعتماد' : 'items pending approval'}
          </Text>
        </View>
        <TouchableOpacity
          style={[styles.backButton, { backgroundColor: isDark ? colors.backgroundAlt : '#F3F4F6' }]}
          onPress={() => navigation.goBack()}
        >
          <ArrowRightIcon size={24} color={themeColors.textPrimary} />
        </TouchableOpacity>
      </View>

      {/* Success Message */}
      {showSuccessMessage && successCounts && (
        <View style={[styles.successBanner, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          <CheckAllIcon size={20} color="#16A34A" />
          <Text style={styles.successText}>
            {t('approval.batchApproveSuccess')}
          </Text>
        </View>
      )}

      {/* Content */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {totalPending === 0 ? (
          <EmptyState
            title={t('approval.noPendingRequests')}
            message={isRTL ? 'تمت معالجة جميع الطلبات' : 'All requests have been processed'}
          />
        ) : (
          <>
            {/* Summary Cards */}
            {activeTypes.map((config) => {
              const count = pendingCounts[config.key];
              return (
                <View
                  key={config.key}
                  style={[
                    styles.typeCard,
                    {
                      backgroundColor: themeColors.card,
                      borderColor: themeColors.cardBorder,
                      flexDirection: isRTL ? 'row-reverse' : 'row',
                    },
                  ]}
                >
                  <View style={[styles.typeIconContainer, { backgroundColor: config.color + '14' }]}>
                    <Svg width={22} height={22} viewBox="0 0 24 24" fill="none">
                      <Path
                        d={config.iconPath}
                        stroke={config.color}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </Svg>
                  </View>
                  <View style={[styles.typeInfo, { alignItems: isRTL ? 'flex-end' : 'flex-start' }]}>
                    <Text style={[styles.typeLabel, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
                      {isRTL ? config.labelAr : config.labelEn}
                    </Text>
                    <Text style={[styles.typeSubLabel, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
                      {t('approval.pendingApproval')}
                    </Text>
                  </View>
                  <View style={[styles.countBadge, { backgroundColor: config.color + '14' }]}>
                    <Text style={[styles.countText, { color: config.color }]}>
                      {count}
                    </Text>
                  </View>
                </View>
              );
            })}

            {/* Total summary */}
            <View style={[styles.totalCard, { backgroundColor: isDark ? colors.backgroundAlt : '#FEF2F2', borderColor: COLORS.primary + '30', flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: 'space-between', alignItems: 'center' }]}>
              <Text style={[styles.totalLabel, { color: themeColors.textPrimary }]}>
                {isRTL ? 'إجمالي العناصر المعلقة' : 'Total Pending Items'}
              </Text>
              <Text style={[styles.totalCount, { color: COLORS.primary }]}>
                {totalPending}
              </Text>
            </View>
          </>
        )}

        {/* Deletion Requests Section */}
        {deletionRequests.length > 0 && (
          <View style={styles.deletionSection}>
            <Text style={[styles.deletionSectionTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
              {isRTL ? 'طلبات الحذف' : 'Deletion Requests'}
            </Text>
            <Text style={[styles.deletionSectionSubtitle, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
              {deletionRequests.length} {isRTL ? 'طلب بانتظار الموافقة' : 'requests pending approval'}
            </Text>

            {deletionRequests.map((request) => (
              <View
                key={request.id}
                style={[styles.deletionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}
              >
                <View style={[styles.deletionCardHeader, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <View style={styles.deletionIconContainer}>
                    <Svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                      <Path d="M3 6H5H21" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                  </View>
                  <View style={[styles.deletionCardInfo, { alignItems: isRTL ? 'flex-end' : 'flex-start' }]}>
                    <Text style={[styles.deletionCardTitle, { color: themeColors.textPrimary }]} numberOfLines={1}>
                      {request.object_title}
                    </Text>
                    <Text style={[styles.deletionCardMeta, { color: themeColors.textMuted }]}>
                      {request.content_type_display} • {request.requested_by_name || (isRTL ? 'مستخدم' : 'User')}
                    </Text>
                  </View>
                </View>
                <View style={[styles.deletionCardActions, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <TouchableOpacity
                    style={styles.rejectButton}
                    onPress={() => handleRejectDeletion(request.id)}
                    disabled={rejectingId === request.id}
                  >
                    {rejectingId === request.id ? (
                      <ActivityIndicator size="small" color="#EF4444" />
                    ) : (
                      <Text style={styles.rejectButtonText}>{isRTL ? 'رفض' : 'Reject'}</Text>
                    )}
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.approveButton}
                    onPress={() => handleApproveDeletion(request.id)}
                    disabled={approvingId === request.id}
                  >
                    {approvingId === request.id ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.approveButtonText}>{isRTL ? 'موافقة' : 'Approve'}</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </ScrollView>

      {/* Approve All Button */}
      {totalPending > 0 && canApprove && (
        <View style={[styles.bottomBar, { backgroundColor: themeColors.card, borderTopColor: themeColors.cardBorder }]}>
          <TouchableOpacity
            style={[styles.approveAllButton, approving && styles.approveAllButtonDisabled]}
            onPress={() => setShowConfirmModal(true)}
            disabled={approving}
          >
            {approving ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <CheckAllIcon size={22} color="#FFFFFF" />
                <Text style={styles.approveAllText}>
                  {t('approval.batchApprove')}
                </Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      )}

      {/* Confirmation Modal */}
      <Modal
        visible={showConfirmModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowConfirmModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, { backgroundColor: themeColors.card }]}>
            <Text style={[styles.modalTitle, { color: themeColors.textPrimary, textAlign: isRTL ? 'right' : 'left' }]}>
              {t('approval.batchApprove')}
            </Text>
            <Text style={[styles.modalMessage, { color: themeColors.textMuted, textAlign: isRTL ? 'right' : 'left' }]}>
              {t('approval.batchApproveConfirm')}
            </Text>

            {/* Summary of what will be approved */}
            <View style={styles.modalSummary}>
              {activeTypes.map((config) => (
                <View key={config.key} style={[styles.modalSummaryRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                  <View style={[styles.modalDot, { backgroundColor: config.color }]} />
                  <Text style={[styles.modalSummaryText, { color: themeColors.textPrimary }]}>
                    {isRTL ? config.labelAr : config.labelEn}
                  </Text>
                  <Text style={[styles.modalSummaryCount, { color: themeColors.textMuted }]}>
                    ({pendingCounts[config.key]})
                  </Text>
                </View>
              ))}
            </View>

            <View style={[styles.modalActions, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
              <TouchableOpacity
                style={styles.modalCancelButton}
                onPress={() => setShowConfirmModal(false)}
              >
                <Text style={styles.modalCancelText}>
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.modalApproveButton}
                onPress={handleBatchApprove}
              >
                <Text style={styles.modalApproveText}>
                  {t('approval.batchApprove')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  backButton: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
  },
  headerContent: {
    flex: 1,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontFamily: FONTS.bold,
    color: '#111827',
  },
  headerSubtitle: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#DCFCE7',
    paddingVertical: 10,
    paddingHorizontal: 16,
    gap: 8,
  },
  successText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#16A34A',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 100,
  },
  typeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 10,
    gap: 12,
  },
  typeIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeInfo: {
    flex: 1,
  },
  typeLabel: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    color: '#111827',
  },
  typeSubLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  countBadge: {
    minWidth: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  countText: {
    fontSize: 16,
    fontFamily: FONTS.numbers,
    fontWeight: '700',
  },
  totalCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    marginTop: 6,
    marginBottom: 16,
  },
  totalLabel: {
    fontSize: 15,
    fontFamily: FONTS.medium,
  },
  totalCount: {
    fontSize: 24,
    fontFamily: FONTS.numbers,
    fontWeight: '700',
  },
  bottomBar: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    backgroundColor: '#FFFFFF',
  },
  approveAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: COLORS.primary,
    borderRadius: 12,
    paddingVertical: 14,
    gap: 8,
  },
  approveAllButtonDisabled: {
    opacity: 0.7,
  },
  approveAllText: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#FFFFFF',
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  modalContent: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    padding: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    marginBottom: 8,
  },
  modalMessage: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    lineHeight: 22,
    marginBottom: 16,
  },
  modalSummary: {
    backgroundColor: '#F9FAFB',
    borderRadius: 10,
    padding: 12,
    marginBottom: 20,
    gap: 8,
  },
  modalSummaryRow: {
    alignItems: 'center',
    gap: 8,
  },
  modalDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  modalSummaryText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    flex: 1,
  },
  modalSummaryCount: {
    fontSize: 13,
    fontFamily: FONTS.numbers,
  },
  modalActions: {
    gap: 10,
  },
  modalCancelButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
  },
  modalCancelText: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    color: '#6B7280',
  },
  modalApproveButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 10,
    backgroundColor: COLORS.primary,
  },
  modalApproveText: {
    fontSize: 15,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  // Deletion Requests Section
  deletionSection: {
    marginTop: 24,
    gap: 12,
  },
  deletionSectionTitle: {
    fontSize: 18,
    fontFamily: FONTS.bold,
    color: '#111827',
  },
  deletionSectionSubtitle: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginBottom: 4,
  },
  deletionCard: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    gap: 12,
  },
  deletionCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  deletionIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#FEE2E2',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deletionCardInfo: {
    flex: 1,
  },
  deletionCardTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#111827',
  },
  deletionCardMeta: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    marginTop: 2,
  },
  deletionCardActions: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'flex-end',
  },
  rejectButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FECACA',
    backgroundColor: '#FEF2F2',
    minHeight: 36,
    minWidth: 70,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectButtonText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#EF4444',
  },
  approveButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#22C55E',
    minHeight: 36,
    minWidth: 70,
    alignItems: 'center',
    justifyContent: 'center',
  },
  approveButtonText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
});
